import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { unwrapSuggestions } from './useSuggestProducts'

/**
 * Seeker-side read of the products the astrologer suggested for one
 * booking — shared by the Suggested Remedies section and the downloadable
 * consultation report, so both show the same rows from one cached request.
 *
 * Primary read is the same list endpoint the astrologer console uses; if
 * that read is refused for a seeker session (or comes back empty), fall
 * back to the customer's own cart filtered by booking_id — suggestions ARE
 * the customer's cart rows, so their own cart always has them.
 */
export function useBookingSuggestions(booking, { live = false } = {}) {
  const bookingId = booking?.id
  const astrologerId = booking?.astrologer_id ?? booking?.astrologer?.id

  return useQuery({
    // Distinct key from the astrologer hook's ['suggested-products', id] —
    // this query caches the unwrapped array, not the raw envelope.
    queryKey: ['customer-suggested-products', bookingId],
    queryFn: async () => {
      if (astrologerId) {
        const rows = await api.suggestedProducts
          .list({ astrologer_id: astrologerId, booking_id: bookingId })
          .then((res) => unwrapSuggestions(res, bookingId))
          .catch(() => null)
        if (rows?.length) return rows
      }
      const cartRes = await api.cart.get().catch(() => null)
      return cartRes ? unwrapSuggestions(cartRes, bookingId) : []
    },
    enabled: Boolean(bookingId),
    // During a live session the astrologer suggests in real time — poll so
    // new suggestions appear without a manual refresh.
    refetchInterval: live ? 15_000 : false,
  })
}
